import { useCallback, useEffect, useState } from 'react';

import { SubnodeAddIcon } from '@hugeicons/core-free-icons';
import { useTracking } from 'hooks/analytic-tracking';
import { useReportingTrends } from 'hooks/use-reporting-trends';
import { Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Alert from 'components/Alert/Alert';
import { Button } from 'components/FormComponents';
import { Table } from 'components/Table/Table';

import { selectCompany } from 'api/company-slice';
import {
  useGetCandidateChecksFiltersQuery,
  useLazyDownloadCandidateChecksReportQuery,
  useLazyGetCandidateChecksReportingQuery,
  useLazyGetCandidateChecksStatsQuery,
  useLazyGetCandidateChecksTrendsQuery,
} from 'api/reporting-api';
import { selectUser } from 'api/user-slice';

import { ReportingChart } from '../../components/ReportingChart/ReportingChart';
import { ReportingWidget } from '../../components/ReportingWidget/ReportingWidget';

const CHECKS_SERIES = [
  { key: 'completed', label: 'Completed', color: '#039d41' },
  { key: 'submitted', label: 'Submitted', color: '#2f80ed' },
  { key: 'in_progress', label: 'In-Progress', color: '#f59e0b' },
  { key: 'candidate', label: 'Candidate', color: '#dfbf0b' },
  { key: 'canceled', label: 'Cancelled', color: '#5c4d78' }, // $neutral-06
];

export const ChecksReporting = ({ companyChecks, locale, setLoading = () => {} }) => {
  const { t } = useTranslation();
  const [getCandidateChecksReporting] = useLazyGetCandidateChecksReportingQuery();
  const [getCandidateChecksStats] = useLazyGetCandidateChecksStatsQuery();
  const [getCandidateChecksTrends] = useLazyGetCandidateChecksTrendsQuery();
  const [downloadCandidateChecksReport] = useLazyDownloadCandidateChecksReportQuery();
  const { data: filters } = useGetCandidateChecksFiltersQuery();

  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState({});
  const [queryParams, setQueryParams] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const [showExportModal, setShowExportModal] = useState(false);

  const trendsFetcher = useCallback(
    async params => getCandidateChecksTrends(params).unwrap(),
    [getCandidateChecksTrends],
  );
  const {
    trends,
    groupBy,
    datePreset,
    datePresets,
    fetchTrends,
    handleGroupByChange,
    handleDatePresetChange,
  } = useReportingTrends(trendsFetcher);

  const fetchChecks = async (params = {}) => {
    setLoading(true);
    setQueryParams(params);
    const result = await getCandidateChecksReporting(params).unwrap();
    const stats = await getCandidateChecksStats(params).unwrap();
    setStats(stats);
    setPagination(result.pagination);
    setChecks(result.candidate_checks);
    try {
      await fetchTrends(params);
    } catch {
      // Trends failed; table and stats are already loaded, don't block UI
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchChecks();
  }, []);

  const download = async (includeSubChecks = false) => {
    setShowExportModal(false);
    setLoading(true);
    await downloadCandidateChecksReport({
      queryParams: { ...queryParams, include_sub_checks: includeSubChecks },
    }).unwrap();
    setLoading(false);
  };

  const filteredSeries = queryParams.status?.length
    ? CHECKS_SERIES.filter(s => queryParams.status.includes(s.key.replace(/_/g, '-')))
    : CHECKS_SERIES;

  return (
    <>
      <Alert
        show={showExportModal}
        title="Include sub-checks in the report?"
        message="<span>If you select <b>Yes</b>, the report will include an additional row for each sub-check, showing its specific details. Would you like to include sub-checks?</span>"
        ok="Yes"
        onOk={() => download(true)}
        type="primary"
        icon={SubnodeAddIcon}
        cancel="No"
        onCancel={() => download(false)}
      />

      <Table.Context onQueryUpdate={fetchChecks} defaultQuery={{ page: 1 }}>
        <Table.FloatingFilters className="u-margin-bottom--large surface-white">
          <Table.FloatingFiltersLeft>
            <Table.DateRangeFilter title="Created within" filterName="created_at" />
            <Table.DateRangeFilter title="Completed within" filterName="completed_at" />
            {company.ff_reporting_billing_filter && (
              <Table.DateRangeFilter title="Billed within" filterName="billed_at" />
            )}
            <Table.MultiSelectFilter
              title="Status"
              filterName="status"
              type="status"
              useDefault={false}
              options={[
                { name: t('common.candidate'), value: 'candidate' },
                { name: t('common.submitted'), value: 'submitted' },
                { name: t('common.inProgress'), value: 'in-progress' },
                { name: t('common.completed'), value: 'completed' },
                { name: t('common.canceled'), value: 'canceled' },
              ]}
            />
            <Table.MultiSelectFilter
              title="In-progress status"
              filterName="in_progress_sub_status"
              type="status"
              useDefault={false}
              options={[
                { name: 'Processing', value: 'processing' },
                { name: 'Waiting on Client', value: 'waiting_on_client' },
                { name: 'Waiting on Candidate', value: 'waiting_on_candidate' },
                { name: 'Waiting on Provider', value: 'waiting_on_provider' },
                { name: 'Blocked', value: 'blocked' },
                { name: 'Deferred', value: 'deferred' },
              ]}
            />
            <Table.MultiSelectFilter
              title="Type"
              filterName="type"
              type="status"
              useDefault={false}
              useSearchInMultiSelect={true}
              options={filters?.types}
            />
            <Table.MultiSelectFilter
              title="Team"
              filterName="team"
              type="status"
              useDefault={false}
              options={filters?.teams}
            />
            <Table.MultiSelectFilter
              title="Has Result"
              filterName="result_flag"
              type="status"
              useDefault={false}
              options={[
                { name: 'Yes', value: true },
                { name: 'No', value: false },
              ]}
            />
            <Table.MultiSelectFilter
              title="Candidate Brand"
              filterName="brand"
              type="status"
              useDefault={false}
              useSearchInMultiSelect={true}
              options={filters?.brands}
            />
          </Table.FloatingFiltersLeft>
          <Table.FloatingFiltersRight>
            <Button icon="download" onClick={() => setShowExportModal(true)}>
              Export
            </Button>
          </Table.FloatingFiltersRight>
        </Table.FloatingFilters>
        <ReportingWidget>
          <ReportingWidget.Tile>
            <ReportingWidget.Badge>Total Checks</ReportingWidget.Badge>
            <ReportingWidget.Value>{stats?.total_checks}</ReportingWidget.Value>
            <ReportingWidget.Label></ReportingWidget.Label>
          </ReportingWidget.Tile>
          <ReportingWidget.Tile>
            <ReportingWidget.Badge>Average Turnaround</ReportingWidget.Badge>
            <ReportingWidget.Value>
              {stats?.avg_turnaround_time}
              <span className="title-3">h</span>
            </ReportingWidget.Value>
            <ReportingWidget.Label></ReportingWidget.Label>
          </ReportingWidget.Tile>
          <ReportingWidget.Divider />
          <ReportingWidget.Tile
            type={ReportingWidget.Types.Candidate}
            disabled={queryParams.status && !queryParams.status.includes('candidate')}>
            <ReportingWidget.Badge>
              Candidate (
              {stats.total_checks == 0
                ? 0
                : Math.round((stats?.candidate * 100) / stats.total_checks)}
              %)
            </ReportingWidget.Badge>
            <ReportingWidget.Value>{stats?.candidate}</ReportingWidget.Value>
            <ReportingWidget.Label>Checks</ReportingWidget.Label>
          </ReportingWidget.Tile>
          <ReportingWidget.Tile
            type={ReportingWidget.Types.InProgress}
            disabled={queryParams.status && !queryParams.status.includes('submitted')}>
            <ReportingWidget.Badge>
              Submitted (
              {stats.total_checks == 0
                ? 0
                : Math.round((stats?.submitted * 100) / stats.total_checks)}
              %)
            </ReportingWidget.Badge>
            <ReportingWidget.Value>{stats?.submitted}</ReportingWidget.Value>
            <ReportingWidget.Label>Checks</ReportingWidget.Label>
          </ReportingWidget.Tile>
          <ReportingWidget.Tile
            type={ReportingWidget.Types.Completed}
            disabled={queryParams.status && !queryParams.status.includes('completed')}>
            <ReportingWidget.Badge>
              Complete (
              {stats.total_checks == 0
                ? 0
                : Math.round((stats?.completed * 100) / stats.total_checks)}
              %)
            </ReportingWidget.Badge>
            <ReportingWidget.Value>{stats?.completed}</ReportingWidget.Value>
            <ReportingWidget.Label>Checks</ReportingWidget.Label>
          </ReportingWidget.Tile>
          <ReportingWidget.Tile
            type={ReportingWidget.Types.Warning}
            disabled={queryParams.status && !queryParams.status.includes('completed')}>
            <ReportingWidget.Badge>
              Has Result (
              {stats.completed == 0 ? 0 : Math.round((stats?.result_flag * 100) / stats.completed)}
              %)
            </ReportingWidget.Badge>
            <ReportingWidget.Value>{stats?.result_flag}</ReportingWidget.Value>
            <ReportingWidget.Label>Checks</ReportingWidget.Label>
          </ReportingWidget.Tile>
          <ReportingWidget.Tile
            type={ReportingWidget.Types.Canceled}
            disabled={queryParams.status && !queryParams.status.includes('canceled')}>
            <ReportingWidget.Badge>
              Cancelled (
              {stats.total_checks == 0
                ? 0
                : Math.round((stats?.canceled * 100) / stats.total_checks)}
              %)
            </ReportingWidget.Badge>
            <ReportingWidget.Value>{stats?.canceled}</ReportingWidget.Value>
            <ReportingWidget.Label>Checks</ReportingWidget.Label>
          </ReportingWidget.Tile>
        </ReportingWidget>
        <ReportingChart
          data={trends}
          series={filteredSeries}
          groupBy={groupBy}
          onGroupByChange={newGroupBy => handleGroupByChange(newGroupBy, queryParams)}
          datePreset={datePreset}
          datePresets={datePresets}
          onDatePresetChange={presetKey => handleDatePresetChange(presetKey, queryParams)}
        />
        <Table.TableContent>
          <Table.Filters>
            <Table.Search placeholder="Search by candidate name..." />
          </Table.Filters>
          <Table className="u-margin--large">
            <Table.Head>
              <Table.Row>
                <Table.Header sortKey="candidate_checks.type">Type</Table.Header>
                <Table.Header sortKey="candidate_checks.status">Status</Table.Header>
                <Table.Header sortKey="candidate_checks.id">ID</Table.Header>
                <Table.Header sortKey="candidates.first_name">Candidate Name</Table.Header>
                <Table.Header sortKey="candidates.email">Email</Table.Header>
                <Table.Header sortKey="candidate_checks.created_at">Created</Table.Header>
                <Table.Header sortKey="candidate_checks.submitted_at">Submitted</Table.Header>
                <Table.Header sortKey="candidate_checks.completed_at">Completed</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {checks.map(check => (
                <Table.Row key={check.id}>
                  <Table.Col className="u-bold">
                    {(companyChecks ?? []).find(cc => cc.type === check.type)?.check_type?.title ??
                      '-'}
                  </Table.Col>
                  <Table.Col>
                    <Badge className={`u-status-${check.status}`}>{check.status}</Badge>
                  </Table.Col>
                  <Table.Col>{check.id}</Table.Col>
                  <Table.Col>
                    <Link className="u-link dark" to={`/check/${check.candidate_id}`}>
                      {check.first_name} {check.last_name}
                    </Link>
                  </Table.Col>
                  <Table.Col>{check.email}</Table.Col>
                  <Table.Col>
                    <Moment format={locale.dateSlashFormat}>{check.created_at}</Moment>
                  </Table.Col>
                  <Table.Col>
                    {check.submitted_at ? (
                      <Moment format={locale.dateSlashFormat}>{check.submitted_at}</Moment>
                    ) : (
                      '-'
                    )}
                  </Table.Col>
                  <Table.Col>
                    {check.completed_at ? (
                      <Moment format={locale.dateSlashFormat}>{check.completed_at}</Moment>
                    ) : (
                      '-'
                    )}
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Pagination {...pagination} />
          </Table>
        </Table.TableContent>
      </Table.Context>
    </>
  );
};
